import React, { useEffect, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Activity, Gauge, RefreshCw } from "lucide-react";

/**
 * DecodoUsageCard — surfaces monthly Decodo Web Scraping API credit burn.
 * Fed by GET /api/admin/decodo/usage. Renders nothing when Decodo isn't
 * configured (no `DECODO_AUTH_TOKEN` in backend/.env) so the admin page
 * stays clean if the fallback provider isn't in use.
 */
export default function DecodoUsageCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/decodo/usage");
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (!loading && (!data || !data.configured)) return null;

  const cur = data?.current_month || { month: "", calls: 0, successes: 0, failures: 0 };
  const cap = data?.free_tier_cap || 667;
  const pct = Math.min(100, Math.round((cur.calls / cap) * 100));
  const tone = pct < 60 ? "bg-emerald-500" : pct < 90 ? "bg-[#D4AF37]" : "bg-red-500";
  const successRate = cur.calls > 0 ? Math.round((cur.successes / cur.calls) * 100) : 0;
  const months = (data?.months || []).slice().reverse();
  const peak = Math.max(1, ...months.map((m) => m.calls));

  return (
    <Card
      className="bg-white border border-[#E9ECEF] rounded-sm p-8"
      data-testid="admin-decodo-usage-card"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-[#D4AF37]" />
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
            Decodo scraper — this month
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#6C757D] hover:text-[#0B132B] flex items-center gap-1.5 disabled:opacity-40"
          data-testid="admin-decodo-usage-refresh"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_2fr] gap-8 items-center">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">
            Calls
          </div>
          <div
            className="mt-1 text-3xl font-black text-[#0B132B] leading-none tabular-nums"
            data-testid="admin-decodo-calls"
          >
            {cur.calls}
            <span className="text-sm font-semibold text-[#6C757D] ml-1">
              / {cap}
            </span>
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#6C757D]">
            {cur.month || "—"} · free tier
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">
            Success
          </div>
          <div
            className="mt-1 text-3xl font-black text-[#0B132B] leading-none tabular-nums"
            data-testid="admin-decodo-success-rate"
          >
            {successRate}
            <span className="text-sm font-semibold text-[#6C757D] ml-1">%</span>
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#6C757D]">
            {cur.successes} ok · {cur.failures} fail
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">
            Burn
          </div>
          <div
            className="mt-1 text-3xl font-black text-[#0B132B] leading-none tabular-nums flex items-baseline gap-1"
            data-testid="admin-decodo-burn-pct"
          >
            <Gauge size={20} className="text-[#D4AF37]" />
            {pct}
            <span className="text-sm font-semibold text-[#6C757D]">%</span>
          </div>
          <div className="mt-2 h-1.5 w-full bg-[#F1F3F5] rounded-full overflow-hidden">
            <div
              className={`h-full ${tone} transition-all`}
              style={{ width: `${pct}%` }}
              data-testid="admin-decodo-burn-bar"
            />
          </div>
        </div>

        {/* 6-month sparkline (bars) */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D] mb-2">
            Last 6 months
          </div>
          <div
            className="flex items-end gap-1.5 h-14"
            data-testid="admin-decodo-history"
          >
            {months.map((m) => {
              const h = Math.max(4, Math.round((m.calls / peak) * 56));
              const isCurrent = m.month === cur.month;
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className={`w-full rounded-sm ${isCurrent ? "bg-[#0B132B]" : "bg-[#D4AF37]/40"}`}
                    style={{ height: `${h}px` }}
                    title={`${m.month}: ${m.calls} calls`}
                  />
                  <div className="text-[9px] font-mono text-[#6C757D]">
                    {m.month.slice(5)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {pct >= 80 && (
        <div className="mt-5 text-xs text-[#6C757D] leading-relaxed">
          <span className="font-semibold text-[#0B132B]">Heads up:</span>{" "}
          burn is <span className="font-semibold text-[#0B132B]">{pct}%</span>{" "}
          of the free-tier cap. Consider upgrading to Core (~$30/mo, ~200K
          premium+JS requests) before the month rolls over.
        </div>
      )}
    </Card>
  );
}
